import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './config';
import InfoTip from './InfoTip';
import './Risk.css';

/* ------------------------------------------------------------------ *
 * Equity > Risk. Everything is computed server-side
 * (portfolio/services/risk_checks.py + risk_overview.py):
 *   GET  /portfolio/risk/overview/   conflicts, capital efficiency, exposure
 *                                    vs limits, stress test, drawdown stop,
 *                                    margin cushion
 *   PUT  /portfolio/risk/limits/     the user's limits
 *   POST /portfolio/risk/pre-trade/  "what would this trade do to my limits?"
 * Telegram alerts are sent by `manage.py check_risk_alerts`.
 * Explanations (Dutch) live in infoGlossary.js and are shown by InfoTip.
 * ------------------------------------------------------------------ */

const eur = (n) => (n === null || n === undefined ? '–'
  : '€' + Number(n).toLocaleString('nl-NL', { maximumFractionDigits: 0 }));
const eur2 = (n) => (n === null || n === undefined ? '–'
  : '€' + Number(n).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const pct = (n, digits = 2) => (n === null || n === undefined ? '–' : `${Number(n).toFixed(digits)}%`);
const signedPct = (n) => (n === null || n === undefined ? '–' : `${n > 0 ? '+' : ''}${Number(n).toFixed(1)}%`);
const STATUS_LABEL = { ok: 'OK', warn: 'Near limit', breach: 'Over limit', unset: 'No limit set' };

/* ---------------------------- Overview cards ---------------------------- */

function DrawdownCard({ dd }) {
  if (!dd || !dd.available) {
    return (
      <section className="risk-card">
        <InfoTip k="dd_stop"><h2>Monthly drawdown stop</h2></InfoTip>
        <p className="risk-muted">Not enough IBKR equity history to calculate this month.</p>
      </section>
    );
  }
  const hasLimit = dd.limit_pct !== null && dd.limit_pct !== undefined && dd.limit_pct > 0;
  const status = !hasLimit ? 'unset' : dd.breached ? 'breach' : dd.drawdown_pct >= dd.limit_pct * 0.75 ? 'near' : 'ok';
  const label = { unset: 'No limit set', breach: 'Stop hit', near: 'Close to limit', ok: 'Within limit' }[status];
  const fill = hasLimit ? Math.min(100, (dd.drawdown_pct / dd.limit_pct) * 100) : 0;

  return (
    <section className={`risk-card risk-status-${status}`}>
      <div className="risk-card-head">
        <InfoTip k="dd_stop"><h2>Monthly drawdown stop</h2></InfoTip>
        <span className={`risk-badge risk-badge-${status}`}>{label}</span>
      </div>
      <div className="risk-stats">
        <InfoTip k="dd_drawdown" extra={`Hoogste stand van de maand: ${dd.peak_date}`}>
          <div><span className="risk-stat-label">Drawdown from {dd.month} high</span><strong>-{pct(dd.drawdown_pct, 1)}</strong></div>
        </InfoTip>
        <InfoTip k="dd_mtd">
          <div><span className="risk-stat-label">Month to date</span><strong>{signedPct(dd.mtd_return_pct)}</strong></div>
        </InfoTip>
        <InfoTip k="dd_limit">
          <div><span className="risk-stat-label">Limit</span><strong>{hasLimit ? pct(dd.limit_pct, 1) : '–'}</strong></div>
        </InfoTip>
      </div>
      {hasLimit && (
        <div className="risk-meter" role="img" aria-label={`${fill.toFixed(0)}% of the drawdown limit used`}>
          <div className="risk-meter-fill" style={{ width: `${fill}%` }} />
        </div>
      )}
      <p className="risk-muted">
        IBKR accounts, deposits and withdrawals excluded. High reached {dd.peak_date}; as of {dd.as_of}.
        {!hasLimit && <> Set a limit under <Link to="/settings">Settings → Goals</Link> to get a Telegram alert.</>}
      </p>
    </section>
  );
}

function ConflictsCard({ conflicts }) {
  return (
    <section className={`risk-card ${conflicts.length ? 'risk-status-near' : ''}`}>
      <div className="risk-card-head">
        <InfoTip k="conflicts"><h2>Conflicts</h2></InfoTip>
        <span className={`risk-badge ${conflicts.length ? 'risk-badge-near' : 'risk-badge-ok'}`}>
          {conflicts.length ? `${conflicts.length} found` : 'None'}
        </span>
      </div>
      {conflicts.length === 0 ? (
        <p className="risk-muted">No short call below a short put, and no hedge expiring within 3 days.</p>
      ) : (
        <ul className="risk-conflicts">
          {conflicts.map((c) => (
            <InfoTip key={c.key} k={c.type === 'strike_inversion' ? 'conflict_inversion' : 'conflict_hedge'}>
              <li className={`risk-conflict risk-conflict-${c.severity}`}>
                <span className="risk-conflict-type">{c.type === 'strike_inversion' ? 'Strike inversion' : 'Hedge expiring'}</span>
                <span>{c.message}</span>
                {c.type === 'strike_inversion' && (
                  <span className="risk-muted">
                    call exp {c.call.expiry} ({c.call.account}) · put exp {c.put.expiry} ({c.put.account})
                  </span>
                )}
                {c.type === 'hedge_expiring' && <span className="risk-muted">{c.put.account}</span>}
              </li>
            </InfoTip>
          ))}
        </ul>
      )}
      <p className="risk-muted">A hedge is any long put. Based on the latest imported positions.</p>
    </section>
  );
}

function CushionSpark({ trend }) {
  if (!trend || trend.length < 2) return null;
  const w = 160, h = 36, pad = 3;
  const vals = trend.map((t) => t.pct);
  const lo = Math.min(...vals), hi = Math.max(...vals);
  const span = hi - lo || 1;
  const pts = trend.map((t, i) => {
    const x = pad + (i / (trend.length - 1)) * (w - 2 * pad);
    const y = h - pad - ((t.pct - lo) / span) * (h - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img"
         aria-label={`Margin cushion over the last ${trend.length} days, from ${lo.toFixed(0)}% to ${hi.toFixed(0)}%`}>
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function ExposureCard({ data }) {
  const { exposure, limits, cushion, stress } = data;
  const t = exposure.totals;
  const configured = limits.configured;
  const names = exposure.by_underlying.filter((n) => n.stock_value || n.put_assignment_value || n.delta_exposure);
  const nameStatus = {};
  limits.items.filter((i) => i.key === 'max_name_weight_pct').forEach((i) => { nameStatus[i.scope] = i.status; });

  return (
    <section className={`risk-card risk-status-${limits.worst === 'warn' ? 'near' : limits.worst}`}>
      <div className="risk-card-head">
        <InfoTip k="exposure_card"><h2>Exposure &amp; limits</h2></InfoTip>
        <span className={`risk-badge risk-badge-${configured ? (limits.worst === 'warn' ? 'near' : limits.worst) : 'unset'}`}>
          {configured ? STATUS_LABEL[limits.worst] : 'No limits set'}
        </span>
      </div>
      {exposure.nav === null || exposure.nav === undefined ? (
        <p className="risk-muted">No equity found, so percentages of NAV cannot be shown.</p>
      ) : (
        <div className="risk-stats">
          <InfoTip k="nav"><div><span className="risk-stat-label">NAV</span><strong>{eur(exposure.nav)}</strong></div></InfoTip>
          <InfoTip k="put_assignment">
            <div><span className="risk-stat-label">Short-put assignment value</span>
              <strong>{eur(t.put_assignment_value)}</strong><span className="risk-muted">{pct(t.put_assignment_pct_nav, 1)} of NAV</span></div>
          </InfoTip>
          <InfoTip k="delta_exposure">
            <div><span className="risk-stat-label">Net delta exposure</span>
              <strong>{eur(t.delta_exposure)}</strong><span className="risk-muted">{pct(t.delta_pct_nav, 1)} of NAV</span></div>
          </InfoTip>
          <InfoTip k="cushion">
            <div><span className="risk-stat-label">Margin cushion</span>
              <strong>{pct(cushion.current_pct, 1)}</strong>
              <span className="risk-muted risk-spark"><CushionSpark trend={cushion.trend} /></span></div>
          </InfoTip>
        </div>
      )}

      {configured && limits.items.length > 0 && (
        <>
          <h3>Against your limits</h3>
          <div className="risk-table-wrap">
            <table className="risk-table">
              <thead><tr><th>Limit</th><th>Scope</th><th className="num">Now</th><th className="num">Limit</th><th>Status</th></tr></thead>
              <tbody>
                {limits.items.filter((i) => i.status !== 'ok' || i.scope === 'total').map((i) => (
                  <tr key={`${i.key}-${i.scope}`}>
                    <td>{i.label}</td><td>{i.scope === 'total' ? 'Portfolio' : i.scope}</td>
                    <td className="num">{pct(i.value, 1)}</td>
                    <td className="num">{i.kind === 'min' ? '≥ ' : '≤ '}{pct(i.limit, 1)}</td>
                    <td><span className={`risk-badge risk-badge-${i.status === 'warn' ? 'near' : i.status}`}>{STATUS_LABEL[i.status]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h3>Per name</h3>
      <div className="risk-table-wrap">
        <table className="risk-table">
          <thead>
            <tr><th>Underlying</th>
              <InfoTip k="th_shares"><th className="num">Shares</th></InfoTip>
              <InfoTip k="th_put_assign"><th className="num">Short-put assignment</th></InfoTip>
              <InfoTip k="th_weight"><th className="num">Weight</th></InfoTip>
              <InfoTip k="th_delta_exposure"><th className="num">Delta exposure</th></InfoTip>
              <InfoTip k="th_pct_nav"><th className="num">% NAV</th></InfoTip></tr>
          </thead>
          <tbody>
            {names.map((n) => (
              <tr key={n.underlying}>
                <td>{n.underlying}{n.missing > 0 && <span className="risk-tag-naked" title="Missing delta or price for some legs">incomplete</span>}</td>
                <td className="num">{eur(n.stock_value)}</td>
                <td className="num">{eur(n.put_assignment_value)}</td>
                <td className="num">
                  {pct(n.weight_pct_nav, 1)}
                  {nameStatus[n.underlying] && nameStatus[n.underlying] !== 'ok' &&
                    <span className={`risk-dot risk-dot-${nameStatus[n.underlying]}`} title={STATUS_LABEL[nameStatus[n.underlying]]} />}
                </td>
                <td className="num">{eur(n.delta_exposure)}</td>
                <td className="num">{pct(n.delta_pct_nav, 1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <InfoTip k="stress_card"><h3>If the market falls</h3></InfoTip>
      <div className="risk-table-wrap">
        <table className="risk-table">
          <thead><tr><th>Scenario</th><th className="num">Volatility</th><th className="num">P&amp;L</th><th className="num">% of NAV</th></tr></thead>
          <tbody>
            {stress.scenarios.map((sc) => (
              <InfoTip key={sc.label} k="stress_row">
                <tr>
                  <td>{sc.label}</td>
                  <td className="num">+{sc.iv_change_pct.toFixed(0)}%</td>
                  <td className="num">{eur(sc.pnl)}</td>
                  <td className="num">{sc.loss_pct_nav === null || sc.loss_pct_nav === undefined ? '–' : `-${pct(sc.loss_pct_nav, 1)}`}</td>
                </tr>
              </InfoTip>
            ))}
          </tbody>
        </table>
      </div>
      {(stress.approximated + stress.skipped > 0) && (
        <p className="risk-muted">
          {stress.approximated} option(s) approximated with delta, {stress.skipped} without price or volatility left out.
        </p>
      )}
      <p className="risk-muted">
        Every stock falls by the same percentage; shares lose that value, options are repriced with a higher volatility.
        Weight = shares + short-put assignment value, as % of NAV. Delta exposure = delta × contracts × multiplier ×
        underlying price (shares count as delta 1). Underlying prices as of {t.oldest_price_at ? new Date(t.oldest_price_at).toLocaleString('nl-NL') : 'unknown'}
        {t.missing_delta + t.missing_price > 0 && ` · ${t.missing_delta} leg(s) without delta, ${t.missing_price} without price are left out of delta exposure`}.
      </p>
    </section>
  );
}

const LIMIT_FIELDS = [
  ['max_put_assignment_pct_nav', 'Max short-put assignment value, % of NAV', 'e.g. 60', 'limit_put'],
  ['max_delta_exposure_pct_nav', 'Max net delta exposure, % of NAV', 'e.g. 100', 'limit_delta'],
  ['max_name_weight_pct', 'Max single-name weight, % of NAV', 'e.g. 25', 'limit_name'],
  ['min_margin_cushion_pct', 'Min margin cushion, %', 'e.g. 25', 'limit_cushion'],
  ['max_stress_loss_pct_nav', 'Max loss in a -20% market drop, % of NAV', 'e.g. 25', 'limit_stress'],
];

function LimitsCard({ current, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const next = {};
    LIMIT_FIELDS.forEach(([key]) => { next[key] = current[key] !== undefined ? String(current[key]) : ''; });
    setForm(next);
  }, [current]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await axios.put(`${API_URL}/portfolio/risk/limits/`, { limits: form });
      setMessage({ ok: true, text: 'Saved.' });
      onSaved();
    } catch (err) {
      setMessage({ ok: false, text: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="risk-card">
      <InfoTip k="limits_card"><h2>Your limits</h2></InfoTip>
      <form onSubmit={save} className="risk-limits-form">
        {LIMIT_FIELDS.map(([key, label, placeholder, tip]) => (
          <InfoTip key={key} k={tip}>
            <label className="risk-field">
              <span>{label}</span>
              <input type="number" step="0.1" min="0" value={form[key] ?? ''} placeholder={placeholder}
                     onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
            </label>
          </InfoTip>
        ))}
        <div className="risk-form-actions">
          <button type="submit" className="risk-button" disabled={saving}>{saving ? 'Saving…' : 'Save limits'}</button>
          {message && <span className={message.ok ? 'risk-muted' : 'risk-error-text'} role="status">{message.text}</span>}
        </div>
      </form>
      <p className="risk-muted">
        Empty = no limit. You get a warning at 80% of a limit and a Telegram alert when one is breached
        (the monthly drawdown stop is set under <Link to="/settings">Settings → Goals</Link>).
      </p>
    </section>
  );
}

function EfficiencyCard({ data }) {
  const [showPositions, setShowPositions] = useState(false);
  const { totals, by_underlying: byUnderlying, positions } = data;

  if (!positions.length) {
    return (
      <section className="risk-card">
        <InfoTip k="eff_card"><h2>Capital efficiency</h2></InfoTip>
        <p className="risk-muted">No short options open.</p>
      </section>
    );
  }
  return (
    <section className="risk-card">
      <div className="risk-card-head"><InfoTip k="eff_card"><h2>Capital efficiency</h2></InfoTip></div>
      <div className="risk-stats">
        <InfoTip k="eff_assignment"><div><span className="risk-stat-label">Assignment value</span><strong>{eur(totals.assignment_value)}</strong></div></InfoTip>
        <InfoTip k="eff_theta"><div><span className="risk-stat-label">Theta / day</span><strong>{eur2(totals.theta_per_day)}</strong></div></InfoTip>
        <InfoTip k="eff_daily"><div><span className="risk-stat-label">Efficiency / day</span><strong>{pct(totals.efficiency_daily_pct, 3)}</strong></div></InfoTip>
        <InfoTip k="eff_annual"><div><span className="risk-stat-label">Annualised</span><strong>{pct(totals.efficiency_annual_pct, 1)}</strong></div></InfoTip>
      </div>
      {totals.missing_theta > 0 && (
        <p className="risk-muted">{totals.missing_theta} position(s) have no theta yet and are left out of the efficiency figures.</p>
      )}

      <h3>Per underlying</h3>
      <div className="risk-table-wrap">
        <table className="risk-table">
          <thead>
            <tr><th>Underlying</th>
              <InfoTip k="th_contracts"><th className="num">Contracts</th></InfoTip>
              <InfoTip k="eff_assignment"><th className="num">Assignment value</th></InfoTip>
              <InfoTip k="th_pct_total"><th className="num">% of total</th></InfoTip>
              <InfoTip k="eff_theta"><th className="num">Theta / day</th></InfoTip>
              <InfoTip k="eff_daily"><th className="num">Eff. / day</th></InfoTip></tr>
          </thead>
          <tbody>
            {byUnderlying.map((g) => (
              <tr key={g.underlying}>
                <td>{g.underlying}</td>
                <td className="num">{g.contracts}</td>
                <td className="num">{eur(g.assignment_value)}</td>
                <td className="num">{pct(g.assignment_pct_of_total, 1)}</td>
                <td className="num">{eur2(g.theta_per_day)}</td>
                <td className="num">{pct(g.efficiency_daily_pct, 3)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button type="button" className="risk-link-button" onClick={() => setShowPositions((v) => !v)} aria-expanded={showPositions}>
        {showPositions ? 'Hide' : 'Show'} all {positions.length} short options
      </button>
      {showPositions && (
        <div className="risk-table-wrap">
          <table className="risk-table">
            <thead>
              <tr><th>Underlying</th><th>Option</th><th>Expiry</th>
                <InfoTip k="th_dte"><th className="num">DTE</th></InfoTip>
                <InfoTip k="th_contracts"><th className="num">Qty</th></InfoTip>
                <InfoTip k="eff_assignment"><th className="num">Assignment value</th></InfoTip>
                <InfoTip k="eff_theta"><th className="num">Theta / day</th></InfoTip>
                <InfoTip k="eff_daily"><th className="num">Eff. / day</th></InfoTip><th>Account</th></tr>
            </thead>
            <tbody>
              {positions.map((p, i) => (
                <tr key={`${p.account}-${p.underlying}-${p.put_call}-${p.strike}-${p.expiry}-${i}`}>
                  <td>{p.underlying}</td>
                  <td>
                    {p.strike} {p.put_call === 'C' ? 'Call' : 'Put'}
                    {p.put_call === 'C' && p.covered === false && <InfoTip k="naked"><span className="risk-tag-naked">naked</span></InfoTip>}
                  </td>
                  <td>{p.expiry}</td>
                  <td className="num">{p.dte ?? '–'}</td>
                  <td className="num">{p.contracts}</td>
                  <td className="num">{eur(p.assignment_value)}</td>
                  <td className="num">{eur2(p.theta_per_day)}</td>
                  <td className="num">{pct(p.efficiency_daily_pct, 3)}</td>
                  <td>{p.account}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="risk-muted">
        Assignment value = strike × multiplier × contracts. Theta / day is the premium decay you collect on the short
        position. USD positions are converted to EUR.
      </p>
    </section>
  );
}

/* ------------------------------ Pre-trade ------------------------------ */

const newLeg = () => ({ underlying: '', type: 'put', side: 'sell', quantity: '1', strike: '', expiry: '', delta: '', fetched: null });
const CONTRACT_FIELDS = ['underlying', 'type', 'strike', 'expiry'];
const VERDICT = {
  ok: ['Within your limits', 'ok', 'This trade does not break a limit or add a conflict (margin impact not included).'],
  unknown: ['Cannot be judged', 'unset', 'The order cannot be placed as entered, or no equity was found. See the notes below.'],
  warn: ['Proceed with care', 'near', 'This trade pushes something near a limit or adds a conflict.'],
  breach: ['Would break a limit', 'breach', 'This trade would breach a limit, make an existing breach worse, or add a serious conflict.'],
};

function PreTrade() {
  const [legs, setLegs] = useState([newLeg()]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Changing the contract invalidates what was fetched for it.
  const update = (i, patch) => setLegs(legs.map((l, idx) => (idx === i
    ? { ...l, ...patch, ...(CONTRACT_FIELDS.some((k) => k in patch) ? { fetched: null } : {}) }
    : l)));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const payload = legs.map((l) => ({
        underlying: l.underlying, type: l.type, side: l.side, quantity: l.quantity,
        ...(l.type !== 'stock' ? { strike: l.strike, expiry: l.expiry, delta: l.delta === '' ? null : l.delta } : {}),
      }));
      const response = await axios.post(`${API_URL}/portfolio/risk/pre-trade/`, { legs: payload });
      setResult(response.data);
      // Show what marketdata.app returned right under each Delta field.
      setLegs((prev) => prev.map((l, idx) => ({
        ...l,
        fetched: (response.data.legs_detail || []).find((d) => d.leg === idx + 1 && d.exists) || null,
      })));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const [verdictLabel, verdictClass, verdictText] = result ? VERDICT[result.verdict] : [];
  const rows = result && [
    ['Short-put assignment value', eur(result.before.put_assignment_value), eur(result.after.put_assignment_value)],
    ['… % of NAV', pct(result.before.put_assignment_pct_nav, 1), pct(result.after.put_assignment_pct_nav, 1)],
    ['Net delta exposure', eur(result.before.delta_exposure), eur(result.after.delta_exposure)],
    ['… % of NAV', pct(result.before.delta_pct_nav, 1), pct(result.after.delta_pct_nav, 1)],
    ['Theta / day', eur2(result.before.theta_per_day), eur2(result.after.theta_per_day)],
    ...result.after.stress.map((sc, i) => [
      `Loss if ${sc.label.toLowerCase()} (vol +${sc.iv_change_pct.toFixed(0)}%), % of NAV`,
      pct(result.before.stress[i].loss_pct_nav, 1), pct(sc.loss_pct_nav, 1), 'stress_row']),
    ['Conflicts', result.before.conflicts, result.after.conflicts],
  ];

  return (
    <>
      <section className="risk-card">
        <InfoTip k="pretrade_card"><h2>Pre-trade check</h2></InfoTip>
        <p className="risk-muted">
          Enter the order you are considering. It is added to your current positions and checked against your limits and
          the conflict rules. Delta, implied volatility and premium are fetched from marketdata.app (the live quote for this
          exact contract) when you click Check trade, and shown under the Delta field; fill in a delta yourself to override it.
        </p>
        <form onSubmit={submit}>
          {legs.map((leg, i) => (
            <fieldset key={i} className="risk-leg">
              <legend>Leg {i + 1}</legend>
              <label className="risk-field"><span>Underlying</span>
                <input required value={leg.underlying} onChange={(e) => update(i, { underlying: e.target.value.toUpperCase() })} placeholder="AAPL" /></label>
              <label className="risk-field"><span>Side</span>
                <select value={leg.side} onChange={(e) => update(i, { side: e.target.value })}>
                  <option value="sell">Sell</option><option value="buy">Buy</option></select></label>
              <label className="risk-field"><span>Type</span>
                <select value={leg.type} onChange={(e) => update(i, { type: e.target.value })}>
                  <option value="put">Put</option><option value="call">Call</option><option value="stock">Shares</option></select></label>
              <label className="risk-field"><span>{leg.type === 'stock' ? 'Shares' : 'Contracts'}</span>
                <input required type="number" min="1" step="1" value={leg.quantity} onChange={(e) => update(i, { quantity: e.target.value })} /></label>
              {leg.type !== 'stock' && (
                <>
                  <label className="risk-field"><span>Strike</span>
                    <input required type="number" step="0.01" min="0" value={leg.strike} onChange={(e) => update(i, { strike: e.target.value })} /></label>
                  <label className="risk-field"><span>Expiry</span>
                    <input required type="date" value={leg.expiry} onChange={(e) => update(i, { expiry: e.target.value })} /></label>
                  <InfoTip k="pt_delta">
                    <label className="risk-field"><span>Delta (optional)</span>
                      <input type="number" step="0.01" min="-1" max="1" value={leg.delta} onChange={(e) => update(i, { delta: e.target.value })}
                             placeholder={leg.fetched && leg.fetched.delta != null ? `auto: ${leg.fetched.delta.toFixed(3)}` : 'auto'} />
                      {leg.fetched && (
                        <small className="risk-muted">
                          marketdata.app: delta {leg.fetched.delta != null ? leg.fetched.delta.toFixed(3) : '–'}
                          {leg.fetched.iv != null && ` · IV ${(leg.fetched.iv * 100).toFixed(0)}%`}
                          {leg.fetched.premium != null && ` · mid $${leg.fetched.premium.toFixed(2)}`}
                        </small>
                      )}
                    </label>
                  </InfoTip>
                </>
              )}
              {legs.length > 1 && (
                <button type="button" className="risk-link-button" onClick={() => setLegs(legs.filter((_, idx) => idx !== i))}>Remove leg</button>
              )}
            </fieldset>
          ))}
          <div className="risk-form-actions">
            {legs.length < 6 && <button type="button" className="risk-button risk-button-secondary" onClick={() => setLegs([...legs, newLeg()])}>Add leg</button>}
            <button type="submit" className="risk-button" disabled={loading}>{loading ? 'Checking…' : 'Check trade'}</button>
          </div>
        </form>
        {error && <div className="risk-error" role="alert">{error}</div>}
      </section>

      {result && (
        <section className={`risk-card risk-status-${verdictClass}`} aria-live="polite">
          <div className="risk-card-head">
            <InfoTip k="pt_verdict"><h2>{verdictLabel}</h2></InfoTip>
            <span className={`risk-badge risk-badge-${verdictClass}`}>{result.verdict.toUpperCase()}</span>
          </div>
          <p>{verdictText}</p>
          <p className="risk-muted">
            Checked against {result.limits_source === 'user' ? 'your limits' : 'starting limits (you have not set your own yet)'}:
            put assignment ≤ {result.limits_used.max_put_assignment_pct_nav}% of NAV, delta ≤ {result.limits_used.max_delta_exposure_pct_nav}%,
            single name ≤ {result.limits_used.max_name_weight_pct}%, margin cushion ≥ {result.limits_used.min_margin_cushion_pct}%,
            loss in a -20% market drop ≤ {result.limits_used.max_stress_loss_pct_nav}%.
            This order alone adds {eur(result.trade_put_assignment_value)} of short-put assignment value
            {result.trade_premium_received > 0 && ` (the ${eur(result.trade_premium_received)} premium you receive is not deducted: limits use the gross, conservative number)`}.
          </p>

          {result.caused.length > 0 && (
            <ul className="risk-conflicts">
              {result.caused.map((c) => (
                <li key={`${c.key}-${c.scope}`} className={`risk-conflict ${c.status === 'breach' ? 'risk-conflict-high' : ''}`}>
                  <span className="risk-conflict-type">{STATUS_LABEL[c.status]}</span>
                  <span>{c.label}{c.scope === 'total' ? '' : ` — ${c.scope}`}: {pct(c.was_value, 1)} → <strong>{pct(c.value, 1)}</strong> (limit {c.kind === 'min' ? '≥' : '≤'} {pct(c.limit, 1)})</span>
                </li>
              ))}
            </ul>
          )}
          {result.new_conflicts.length > 0 && (
            <ul className="risk-conflicts">
              {result.new_conflicts.map((c) => (
                <li key={c.key} className={`risk-conflict risk-conflict-${c.severity}`}>
                  <span className="risk-conflict-type">New conflict</span><span>{c.message}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="risk-table-wrap">
            <table className="risk-table">
              <thead><tr><th>Portfolio</th><th className="num">Before</th><th className="num">After</th></tr></thead>
              <tbody>{rows.map(([label, a, b, tip], i) => (
                <tr key={i}>
                  {tip ? <InfoTip k={tip}><td>{label}</td></InfoTip> : <td>{label}</td>}
                  <td className="num">{a}</td><td className="num">{b}</td>
                </tr>))}</tbody>
            </table>
          </div>
          {(result.context_notes || []).map((n) => <p key={n} className="risk-context">{n}</p>)}
          {result.warnings.map((w) => <p key={w} className="risk-muted">• {w}</p>)}
        </section>
      )}

      {result && result.legs_detail && result.legs_detail.some((d) => d.exists) && (
        <section className="risk-card">
          <h2>About this order</h2>
          <div className="risk-table-wrap">
            <table className="risk-table">
              <thead>
                <tr><th>Contract</th><th className="num">Price</th>
                  <InfoTip k="ld_pct_otm"><th className="num">OTM</th></InfoTip>
                  <InfoTip k="ld_premium"><th className="num">Premium</th></InfoTip>
                  <InfoTip k="ld_prob_itm"><th className="num">Chance ITM at expiry</th></InfoTip>
                  <InfoTip k="ld_breakeven"><th className="num">Break-even</th></InfoTip>
                  <InfoTip k="ld_yield"><th className="num">Premium / year</th></InfoTip>
                  <InfoTip k="ld_max_loss"><th className="num">Max loss</th></InfoTip>
                  <InfoTip k="ld_theta"><th className="num">Theta / day</th></InfoTip></tr>
              </thead>
              <tbody>
                {result.legs_detail.filter((d) => d.exists).map((d) => (
                  <tr key={d.leg}>
                    <td>{d.side === 'sell' ? 'Sell' : 'Buy'} {d.contracts} × {d.label} <span className="risk-muted">({d.dte} days)</span></td>
                    <td className="num">{d.spot != null ? `$${d.spot.toFixed(2)}` : '–'}</td>
                    <td className="num">{pct(d.pct_otm, 0)}</td>
                    <td className="num">{d.premium != null ? `$${d.premium.toFixed(2)}` : '–'}<br />
                      <span className="risk-muted">{d.premium_total != null ? eur(d.premium_total) : ''}</span></td>
                    <td className="num">{pct(d.prob_itm_pct, 0)}</td>
                    <td className="num">{d.breakeven != null ? `$${d.breakeven.toFixed(2)}` : '–'}</td>
                    <td className="num">{pct(d.annual_yield_pct, 1)}</td>
                    <td className="num">{d.max_loss != null ? eur(d.max_loss) : '–'}</td>
                    <td className="num">{d.theta_per_day != null ? eur2(d.theta_per_day) : '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="risk-muted">
            The chance of finishing in the money is what the option market itself implies (from the option's volatility). It is not
            the delta, and a long-dated put can be worth much more on paper well before expiry if the market falls: see the loss rows above.
          </p>
        </section>
      )}
    </>
  );
}

/* --------------------------------- Page --------------------------------- */

function Risk() {
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/portfolio/risk/overview/`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="risk-page">
      <div className="risk-toolbar">
        <div role="tablist" aria-label="Risk sections" className="risk-tabs">
          {[['overview', 'Overview'], ['pretrade', 'Pre-trade check']].map(([key, label]) => (
            <InfoTip key={key} k={key === 'overview' ? 'tab_overview' : 'tab_pretrade'}>
              <button type="button" role="tab" aria-selected={tab === key}
                      className={`risk-tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>{label}</button>
            </InfoTip>
          ))}
        </div>
        {tab === 'overview' && (
          <InfoTip k="refresh">
            <button type="button" className="risk-link-button" onClick={load} disabled={loading}>
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </InfoTip>
        )}
      </div>

      {tab === 'pretrade' ? <PreTrade /> : (
        <>
          {error && <div className="risk-error" role="alert">Could not load risk data: {error}</div>}
          {!data && loading && <p className="risk-muted">Loading…</p>}
          {data && (
            <>
              <div className="risk-top">
                <DrawdownCard dd={data.drawdown} />
                <ConflictsCard conflicts={data.conflicts} />
              </div>
              <ExposureCard data={data} />
              <LimitsCard current={data.limit_settings} onSaved={load} />
              <EfficiencyCard data={data.capital_efficiency} />
            </>
          )}
        </>
      )}
    </div>
  );
}

export default Risk;
